import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Search, Workflow, Database, Plug, Check, PhoneCall, MousePointerClick, UserPlus, FileText, Users, Smartphone, Sparkles } from 'lucide-react';
import PageHero from '../components/PageHero';
import CtaBanner from '../components/CtaBanner';
import FaqAccordion from '../components/FaqAccordion';
import Seo from '../components/Seo';
import { CRM_LOGOS } from '../data/mock';

const CRM_FAQ = [
  { q: 'How many CRMs do you support?', a: 'Over 200 popular cross-vertical and vertical-specific applications work out of the box via Go Integrator NAVA — Salesforce, HubSpot, Zoho, Bitrix24, Microsoft Dynamics, Sage, Xero, Pipedrive, Capsule, Insightly, and many more.' },
  { q: 'What is Go Integrator NAVA?', a: 'NAVA is a desktop softphone + presence + CRM integration client from our partner Mondago, purpose-built for the VoIPinger platform. It runs alongside whatever CRM you already use and adds click-to-dial, contact popping, activity logging and presence.' },
  { q: 'Do we need to install anything?', a: 'NAVA installs on each user\'s desktop (Windows + macOS). No plugin required for the CRM itself — NAVA hooks into the CRM via its public API or accessibility layer.' },
  { q: 'Does this work with Microsoft Teams as the softphone?', a: 'Yes. NAVA has deep MS Teams integration — use Teams as your preferred device, show/add/pop contact from Teams to your CRM, surface Teams presence and avatars inside NAVA. Combined with TeamMate Direct Routing this is a complete Teams-first stack.' },
  { q: "Do you log calls to the CRM automatically?", a: 'Either manually or automatically — both are supported. Automatic mode creates an activity record for every call (inbound or outbound) with caller, callee, duration, recording URL and disposition.' },
  { q: 'Can we click-to-dial from a CRM record?', a: 'Yes. Any phone number rendered in a supported CRM becomes clickable — NAVA picks up the click and dials it from your preferred device (desk phone, mobile, softphone or Teams).' }
];

const MORE_LOGOS = [
  ...CRM_LOGOS,
  { name: 'Salesforce', img: '/assets/7.png' },
  { name: 'HubSpot', img: '/assets/6.png' },
  { name: 'Microsoft Teams', img: '/assets/5.png' },
  { name: 'Xero', img: '/assets/4-1.png' }
];

export default function CrmIntegrations() {
  const [q, setQ] = useState('');
  const filtered = MORE_LOGOS.filter((l) => l.name.toLowerCase().includes(q.toLowerCase()));

  return (
    <>
      <Seo
        title="CRM Integrations — 250+ supported apps via Go Integrator NAVA | VoIPinger"
        description="Click-to-dial, contact popping, activity logging and presence inside Salesforce, HubSpot, Zoho, Microsoft Teams and 200+ other apps via Go Integrator NAVA."
        path="/crm-integrations"
        image="https://voipinger.com/assets/heroes/crm-integrations.png"
        service={{ name: 'Telephony CRM Integration', serviceType: 'CTI / CRM telephony integration' }}
        faq={CRM_FAQ}
        breadcrumbs={[{ name: 'Home', path: '/' }, { name: 'CRM Integrations', path: '/crm-integrations' }]}
      />
      <PageHero
        eyebrow="Telephony CRM Integration"
        title="Connect VoIPinger with 250+ CRMs"
        subtitle="CRM integration helps your team collaborate better, get insights faster and run workflows automatically — at the right cost and with simple setup."
        breadcrumb="CRM Integrations"
      />

      <section className="bg-white">
        <div className="container-x section-pad">
          <div className="max-w-3xl mx-auto text-center mb-10">
            <span className="eyebrow">Search Integrations</span>
            <h2 className="section-title mt-4">Find your CRM</h2>
            <p className="section-sub mt-3">Type a name to find your favourite tool. Don’t see it? Reach out — we add new integrations every month.</p>
            <div className="mt-7 max-w-xl mx-auto relative">
              <Search size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search HubSpot, Salesforce, Zoho..."
                className="w-full pl-11 pr-4 py-3.5 rounded-full border border-slate-200 focus:border-violet-500 focus:ring-4 focus:ring-violet-100 outline-none"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
            {filtered.map((l, i) => (
              <div
                key={`${l.name}-${i}`}
                className="group bg-white rounded-2xl border border-slate-200 hover:border-violet-300 hover:shadow-xl transition-all p-5 flex flex-col items-center gap-3"
              >
                <div className="w-full aspect-square rounded-xl bg-slate-50 flex items-center justify-center p-4 group-hover:bg-violet-50 transition-colors">
                  <img src={l.img} alt={l.name} className="max-h-16 w-auto object-contain grayscale group-hover:grayscale-0 transition-all" loading="lazy" />
                </div>
                <p className="text-sm font-semibold text-slate-700 text-center">{l.name}</p>
              </div>
            ))}
          </div>
          {filtered.length === 0 && (
            <p className="text-center text-slate-500 mt-8">No integrations found. Try a different search term.</p>
          )}
        </div>
      </section>

      {/* Why CRM */}
      <section className="bg-slate-50 border-y border-slate-100">
        <div className="container-x section-pad grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <span className="eyebrow">CRM Integration</span>
            <h2 className="section-title mt-4">Run smarter with telephony in your CRM</h2>
            <p className="section-sub mt-4">
              Today, organisations analyse data about their relationships and interactions with potential
              and existing customers to build better strategies. CRM integration brings the phone into the
              same workflow — every call automatically logged, contextualised and ready for follow-up.
            </p>
            <ul className="mt-6 grid sm:grid-cols-2 gap-2">
              {['Click-to-dial from any record', 'Auto-create activities & notes', 'Screen pop on inbound calls', 'Real-time analytics', 'Two-way contact sync', 'Workflow automation'].map((b) => (
                <li key={b} className="flex items-start gap-2 text-slate-700">
                  <Check size={16} className="text-violet-600 mt-1" />
                  <span className="text-sm">{b}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="grid grid-cols-3 gap-4">
            {[Workflow, Database, Plug].map((Ic, i) => (
              <div key={i} className="col-span-1 first:col-span-2 first:row-span-2 first:aspect-square aspect-square rounded-2xl bg-white border border-slate-200 flex items-center justify-center text-violet-600 hover:scale-105 transition-transform">
                <Ic size={i === 0 ? 56 : 28} />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============================================================== */}
      {/* GO INTEGRATOR NAVA — the engine                                  */}
      {/* ============================================================== */}
      <section className="relative overflow-hidden bg-gradient-to-b from-white via-violet-50/30 to-white">
        <div className="container-x section-pad">
          <div className="max-w-3xl mx-auto text-center mb-12">
            <span className="eyebrow"><Sparkles size={11} /> Powered by Go Integrator NAVA</span>
            <h2 className="section-title mt-4">The integration engine behind every CRM connector</h2>
            <p className="section-sub mt-3">
              NAVA is the desktop client from our partner Mondago, built specifically for the
              VoIPinger platform. It sits next to whatever CRM your team already uses and
              quietly upgrades it with telephony, presence and call control.
            </p>
            <div className="divider-svg mt-6" />
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 max-w-6xl mx-auto">
            {[
              { i: Search, t: 'Contact searching', d: 'Searches every integrated CRM simultaneously. Find a contact in HubSpot, Salesforce and Zoho from one search box.' },
              { i: MousePointerClick, t: 'Click-to-dial', d: 'Any phone number in your CRM becomes clickable. NAVA picks it up and dials from your preferred device.' },
              { i: UserPlus, t: 'Add contact / account', d: 'Add a new contact or account record to your CRM directly from the NAVA caller preview window — no tab switching.' },
              { i: Workflow, t: 'Contact popping', d: 'Automatically pop the matching customer record the moment a call rings — no more searching mid-call.' },
              { i: FileText, t: 'Activity logging', d: 'Manually or automatically create an activity record for every call. Add notes during or after.' },
              { i: Users, t: 'Presence', d: 'See colleagues\' extension status. Make a call, transfer, or pick up a co-worker\'s ringing line from the presence window.' },
              { i: Smartphone, t: 'Preferred device', d: 'Pick which device the next call comes from / goes out on — desk phone, mobile, web phone, or MS Teams.' },
              { i: PhoneCall, t: 'Built-in softphone', d: "NAVA ships with its own softphone, so it works even when you don't have a desk phone or a Teams licence." },
              { i: Plug, t: 'Microsoft Teams deep integration', d: 'Use Teams as preferred device. Show/add/pop contacts from Teams to your CRM. Avatars and presence flow both directions.' }
            ].map((c) => (
              <div key={c.t} className="rounded-2xl bg-white border border-slate-200 p-5 hover:border-violet-300 hover:shadow-md hover:-translate-y-0.5 transition-all">
                <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-violet-500 to-fuchsia-500 text-white flex items-center justify-center mb-4 shadow-md shadow-violet-500/20">
                  <c.i size={20} />
                </div>
                <h3 className="font-extrabold text-slate-900 mb-2">{c.t}</h3>
                <p className="text-sm text-slate-600 leading-relaxed">{c.d}</p>
              </div>
            ))}
          </div>

          <div className="max-w-3xl mx-auto mt-10 text-center">
            <p className="text-sm text-slate-500">
              <strong className="text-slate-900">200+ supported applications</strong> ·
              cross-vertical (Salesforce, HubSpot, Dynamics, Zoho, Pipedrive…) and
              vertical-specific (legal, healthcare, real-estate, accounting…) — all bundled in one NAVA seat.
            </p>
            <div className="mt-6 flex flex-wrap items-center justify-center gap-3">
              <Link to="/contact" className="btn-primary">Talk to a NAVA specialist <ArrowRight size={14} /></Link>
              <Link to="/microsoft-teams" className="btn-ghost">See Teams integration</Link>
            </div>
          </div>
        </div>
      </section>

      <FaqAccordion items={CRM_FAQ} title="CRM Integrations — FAQ" />

      <CtaBanner title="Need an integration we don't list yet?" subtitle="Our engineers build new connectors monthly. Tell us what you use and we'll quote you in 24 hours." />
    </>
  );
}
